import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Modal, TextButton } from '@safetyhub/ui';
import { adminApi, extractApiError } from '../lib/api';
import { DEFAULT_MEMBER_ROLE_KEY, OWNER_ROLE_KEY, useSystemRoleOptions } from '../lib/roles';
import { ModalCancelButton } from './ModalCancelButton';

interface CreateUserDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface NewOrganization {
  name: string;
}

interface MembershipEntry {
  type: 'existing' | 'new';
  /** 기존 조직 선택 시의 회사 id — 서버 `CreateUserMembershipDto.companyId`와 같은 축. */
  companyId?: string;
  newOrganization?: NewOrganization;
  /** 시스템 역할 key(P7-b-2 — 옛 `role` enum 대체) */
  roleKey: string;
}

export default function CreateUserDialog({
  isOpen,
  onClose,
  onSuccess,
}: CreateUserDialogProps) {
  const queryClient = useQueryClient();

  // 폼 상태
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [memberships, setMemberships] = useState<MembershipEntry[]>([]);
  const [error, setError] = useState<string | null>(null);

  // 조직(구독) 목록 조회 (GROUP 조직만)
  const { data: orgsData, isLoading: orgsLoading } = useQuery({
    queryKey: ['admin', 'subscriptions'],
    queryFn: () => adminApi.getSubscriptions({ limit: 100 }),
    enabled: isOpen,
  });

  // getSubscriptions는 이미 비즈니스 조직(category != WORKSPACE)만 반환
  const businessOrgs = orgsData?.data || [];
  // 역할 select 후보 — 활성 시스템 역할(`/admin/rbac/roles`)
  const { options: roleOptions } = useSystemRoleOptions(isOpen);

  // 다이얼로그 열릴 때 초기화
  useEffect(() => {
    if (isOpen) {
      setName('');
      setPhone('');
      setUsername('');
      setPassword('');
      setMemberships([]);
      setError(null);
    }
  }, [isOpen]);

  // 사용자 생성 mutation
  const createMutation = useMutation({
    mutationFn: () => {
      // memberships를 API 형식으로 변환
      const apiMemberships = memberships.map((m) => {
        if (m.type === 'new' && m.newOrganization) {
          return {
            newOrganization: {
              name: m.newOrganization.name,
            },
            roleKey: m.roleKey,
          };
        } else {
          return {
            companyId: m.companyId,
            roleKey: m.roleKey,
          };
        }
      });

      return adminApi.createUser({
        name,
        phone,
        username,
        password,
        memberships: apiMemberships.length > 0 ? apiMemberships : undefined,
      });
    },
    onSuccess: (data) => {
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
        queryClient.invalidateQueries({ queryKey: ['admin', 'subscriptions'] });
        onSuccess();
        onClose();
      } else {
        setError(data.error?.message || '생성에 실패했습니다.');
      }
    },
    onError: (err) => {
      setError(extractApiError(err).message);
    },
  });

  const handleAddExistingOrg = () => {
    // 아직 선택되지 않은 첫 번째 조직 선택
    // 중복 방지 Set과 비교 대상 모두 회사 id 축으로 통일한다.
    const selectedCompanyIds = new Set(
      memberships.filter((m) => m.type === 'existing').map((m) => m.companyId)
    );
    const availableOrg = businessOrgs.find((org) => !selectedCompanyIds.has(org.companyId));

    if (availableOrg) {
      setMemberships([
        ...memberships,
        { type: 'existing', companyId: availableOrg.companyId, roleKey: DEFAULT_MEMBER_ROLE_KEY },
      ]);
    }
  };

  const handleAddNewOrg = () => {
    setMemberships([
      ...memberships,
      {
        type: 'new',
        newOrganization: { name: '' },
        roleKey: OWNER_ROLE_KEY, // 새 조직의 경우 기본적으로 소유자
      },
    ]);
  };

  const handleRemoveMembership = (index: number) => {
    setMemberships(memberships.filter((_, i) => i !== index));
  };

  const handleMembershipChange = (
    index: number,
    field: 'companyId' | 'roleKey',
    value: string
  ) => {
    const updated = [...memberships];
    if (field === 'companyId') {
      updated[index].companyId = value;
    } else {
      updated[index].roleKey = value;
    }
    setMemberships(updated);
  };

  const handleNewOrgChange = (
    index: number,
    field: keyof NewOrganization,
    value: string
  ) => {
    const updated = [...memberships];
    if (updated[index].newOrganization) {
      updated[index].newOrganization = {
        ...updated[index].newOrganization!,
        [field]: value,
      };
    }
    setMemberships(updated);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // 기본 검증
    if (!name.trim()) {
      setError('이름을 입력해주세요.');
      return;
    }
    if (!phone.trim()) {
      setError('휴대폰 번호를 입력해주세요.');
      return;
    }
    if (!username.trim()) {
      setError('로그인 아이디를 입력해주세요.');
      return;
    }
    if (username.length < 4) {
      setError('아이디는 최소 4자 이상이어야 합니다.');
      return;
    }
    if (!password) {
      setError('비밀번호를 입력해주세요.');
      return;
    }
    if (password.length < 8) {
      setError('비밀번호는 최소 8자 이상이어야 합니다.');
      return;
    }

    // 새 조직 이름 검증
    for (const m of memberships) {
      if (m.type === 'new' && (!m.newOrganization?.name || m.newOrganization.name.trim().length < 2)) {
        setError('새 조직의 이름은 최소 2자 이상이어야 합니다.');
        return;
      }
    }

    createMutation.mutate();
  };

  // 이미 선택된 조직 ID 목록 (회사 id 축)
  const selectedCompanyIds = new Set(
    memberships.filter((m) => m.type === 'existing').map((m) => m.companyId)
  );

  // 추가 가능한 기존 조직이 있는지
  const hasAvailableOrgs = businessOrgs.some((org) => !selectedCompanyIds.has(org.companyId));

  // 미저장 닫기 보호 — 열 때마다 빈 폼으로 리셋하므로(위 useEffect) 기준선은 빈 폼이다.
  const isDirty =
    name !== '' || phone !== '' || username !== '' || password !== '' || memberships.length > 0;

  return (
    // 생성 중에는 닫는 길(오버레이·Esc·✕) 전부를 막는다 — 응답 전에 닫히면 결과를 볼 자리가 사라진다.
    <Modal
      open={isOpen}
      onClose={onClose}
      title="사용자 생성"
      size="md"
      closeOnOverlay={!createMutation.isPending}
      closeOnEsc={!createMutation.isPending}
      showCloseButton={!createMutation.isPending}
      closeLocked={createMutation.isPending}
      isDirty={isDirty}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* 이름 */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            이름 <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-control focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            placeholder="홍길동"
          />
        </div>

        {/* 휴대폰 번호 */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            휴대폰 번호 <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/[^0-9]/g, ''))}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-control focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            placeholder="01012345678"
          />
        </div>

        {/* 로그인 아이디 */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            로그인 아이디 <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-control focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            placeholder="testuser1"
          />
          <p className="mt-1 text-xs text-gray-500">영문, 숫자, 밑줄 또는 이메일 형식 (4자 이상)</p>
        </div>

        {/* 비밀번호 */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            비밀번호 <span className="text-red-500">*</span>
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-control focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            placeholder="********"
          />
          <p className="mt-1 text-xs text-gray-500">8자 이상, 영문/숫자/특수문자 포함</p>
        </div>

        {/* 조직 멤버십 섹션 */}
        <div className="pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-gray-700">
              조직 멤버십 (선택)
            </label>
            <div className="flex gap-2">
              {hasAvailableOrgs && (
                <TextButton onClick={handleAddExistingOrg} className="text-sm">
                  + 기존 조직
                </TextButton>
              )}
              {/* 초록은 "새 조직 생성" 갈래 표시(아래 새 조직 카드가 green-50) — brand와 색으로 가른다. */}
              <button
                type="button"
                onClick={handleAddNewOrg}
                className="text-sm text-green-600 hover:text-green-800"
              >
                + 새 조직
              </button>
            </div>
          </div>
          <p className="text-xs text-gray-500 mb-3">
            사용자를 특정 조직에 추가할 수 있습니다. 선택하지 않으면 개인 조직만 생성됩니다.
          </p>

          {orgsLoading ? (
            <div className="text-sm text-gray-500">조직 목록 로딩 중...</div>
          ) : memberships.length === 0 ? (
            <div className="p-4 border border-dashed border-gray-300 rounded-card text-center">
              <p className="text-sm text-gray-500">
                추가할 조직이 없습니다.
                <br />
                <span className="inline-flex gap-2 mt-2">
                  {hasAvailableOrgs && (
                    <TextButton onClick={handleAddExistingOrg}>+ 기존 조직 추가</TextButton>
                  )}
                  <button
                    type="button"
                    onClick={handleAddNewOrg}
                    className="text-green-600 hover:text-green-800"
                  >
                    + 새 조직 생성
                  </button>
                </span>
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {memberships.map((membership, index) => (
                <div
                  key={index}
                  className={`p-3 rounded-card ${
                    membership.type === 'new' ? 'bg-green-50 border border-green-200' : 'bg-gray-50'
                  }`}
                >
                  {membership.type === 'existing' ? (
                    // 기존 조직 선택
                    <div className="flex items-center gap-2">
                      <select
                        value={membership.companyId}
                        onChange={(e) =>
                          handleMembershipChange(index, 'companyId', e.target.value)
                        }
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-control focus:ring-2 focus:ring-brand-500"
                      >
                        {businessOrgs
                          .filter(
                            (org) =>
                              org.companyId === membership.companyId ||
                              !selectedCompanyIds.has(org.companyId)
                          )
                          .map((org) => (
                            // value는 회사 id — 서버 DTO의 companyId가 받는 축.
                            <option key={org.id} value={org.companyId}>
                              {org.name}
                            </option>
                          ))}
                      </select>
                      <select
                        value={membership.roleKey}
                        onChange={(e) =>
                          handleMembershipChange(index, 'roleKey', e.target.value)
                        }
                        className="w-32 px-3 py-2 text-sm border border-gray-300 rounded-control focus:ring-2 focus:ring-brand-500"
                      >
                        {roleOptions.map((role) => (
                          <option key={role.key} value={role.key}>
                            {role.name}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => handleRemoveMembership(index)}
                        className="p-2 text-gray-400 hover:text-red-500"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    // 새 조직 생성
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-green-700">새 조직 생성</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveMembership(index)}
                          className="p-1 text-gray-400 hover:text-red-500"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </div>
                      <input
                        type="text"
                        value={membership.newOrganization?.name || ''}
                        onChange={(e) => handleNewOrgChange(index, 'name', e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-control focus:ring-2 focus:ring-green-500"
                        placeholder="조직 이름 *"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 에러 메시지 */}
        {error && (
          <div className="p-3 bg-red-50 text-red-600 text-sm rounded-card">{error}</div>
        )}

        {/* 액션 버튼 */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <ModalCancelButton onCancel={onClose} disabled={createMutation.isPending} />
          <Button type="submit" variant="primary" disabled={createMutation.isPending}>
            {createMutation.isPending ? '생성 중...' : '생성'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
